import React, { useEffect, useState, useCallback } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { History, LogIn, LogOut } from "lucide-react";

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ImpersonationAuditPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/impersonation-audit", { params: { limit: 200 } });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <DashboardShell>
            <div className="fade-up" data-testid="impersonation-audit-page">
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                    Super admin · Audit
                </p>
                <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-900 ring-1 ring-blue-100">
                        <History size={18} />
                    </span>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        Impersonation log
                    </h1>
                </div>
                <p className="mt-3 max-w-2xl text-sm text-slate-600 leading-relaxed">
                    Every "Log in as" start and stop, newest first. Total entries: {total}.
                    Capped at the most recent 200.
                </p>
            </div>

            <div className="mt-8">
                <Card>
                    <CardHeader>
                        <CardTitle className="font-display text-xl">Events</CardTitle>
                        <CardDescription>
                            Read-only record of who accessed whose account, when, and from which IP.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="text-sm text-slate-500" data-testid="imp-audit-loading">Loading…</div>
                        ) : items.length === 0 ? (
                            <div className="text-sm text-slate-500" data-testid="imp-audit-empty">No impersonation events yet.</div>
                        ) : (
                            <div className="overflow-x-auto" data-testid="imp-audit-list">
                                <table className="min-w-full text-sm">
                                    <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                        <tr>
                                            <th className="py-2 pr-4">When</th>
                                            <th className="py-2 pr-4">Action</th>
                                            <th className="py-2 pr-4">Actor (super admin)</th>
                                            <th className="py-2 pr-4">Target</th>
                                            <th className="py-2 pr-4">Role</th>
                                            <th className="py-2 pr-4">IP</th>
                                            <th className="py-2 pr-4">User agent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((it) => (
                                            <tr key={it.id} data-testid={`imp-audit-row-${it.id}`} className="border-b border-slate-100 last:border-0">
                                                <td className="py-2 pr-4 text-slate-600 whitespace-nowrap">{fmt(it.timestamp)}</td>
                                                <td className="py-2 pr-4">
                                                    {it.action === "start" ? (
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-800 ring-1 ring-blue-100 px-2 py-0.5 text-[11px] font-semibold uppercase">
                                                            <LogIn size={11} /> start
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100 px-2 py-0.5 text-[11px] font-semibold uppercase">
                                                            <LogOut size={11} /> stop
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-2 pr-4 font-mono text-xs">{it.actor_email}</td>
                                                <td className="py-2 pr-4 font-mono text-xs">{it.target_email}</td>
                                                <td className="py-2 pr-4 text-slate-700">{it.target_role}</td>
                                                <td className="py-2 pr-4 font-mono text-xs">{it.ip || "—"}</td>
                                                <td className="py-2 pr-4 font-mono text-[10px] text-slate-500 max-w-[260px] truncate" title={it.user_agent || ""}>
                                                    {it.user_agent || "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </DashboardShell>
    );
}
